import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { tokenService } from './tokenService';

// Marker fuer Requests, die bereits einmal nach einem 401 retried wurden —
// schuetzt vor Endlos-Loops, wenn der refreshte Token AUCH 401 zurueckkommt.
type RetryableConfig = AxiosRequestConfig & { _retryAfterRefresh?: boolean };

/**
 * Response-Error-Handler fuer den 401-Refresh-Flow (#295). Aus api.ts in eine
 * eigene Datei extrahiert, damit Jest das Modul ohne axios-ESM-Import laden
 * kann (CRA-Jest kann axios v1 nicht transformieren — `import type` wird zur
 * Compile-Zeit gestrippt und triggert keinen Runtime-Resolve).
 *
 * Verhalten:
 *  - Bei 401 + kein vorheriger Retry: Refresh-Token einloesen, Original-Request
 *    mit neuem Bearer retryen.
 *  - Bei 401 + bereits retried: Tokens aufraeumen, Fehler durchreichen.
 *  - Refresh-Fail (kein Refresh-Token, 4xx vom SSO): Tokens aufraeumen,
 *    Fehler durchreichen.
 *  - Nicht-401-Fehler: nur durchreichen, Tokens bleiben.
 */
export async function handleResponseError(
    error: AxiosError,
    retry: (config: AxiosRequestConfig) => Promise<AxiosResponse>,
): Promise<AxiosResponse> {
    const config = error.config as RetryableConfig | undefined;
    if (error.response?.status !== 401 || !config || config._retryAfterRefresh) {
        if (error.response?.status === 401) {
            tokenService.removeToken();
            tokenService.removeRefreshToken();
        }
        throw error;
    }

    config._retryAfterRefresh = true;
    const refreshed = await tokenService.refreshAccessToken();
    if (!refreshed) {
        tokenService.removeToken();
        tokenService.removeRefreshToken();
        throw error;
    }

    const newToken = tokenService.getToken();
    if (newToken && config.headers) {
        (config.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
    }
    return retry(config);
}
